/**
 * 🐋 Species Utilities - Cetalingua FM
 * Target path: /src/shared/speciesUtils.js
 * 
 * Bridges speciesData.js (the 88-species source of truth) with the
 * advertiser content system. Used by:
 * - SpeciesSelectScreen.tsx (app onboarding)
 * - CompanyProfile.js (advertiser dashboard)
 * - AdminDashboard.js (admin panel)
 * - WeeklyContentManager.js (content targeting)
 * - CompanyDetailScreen.js (app display)
 * 
 * All species are referenced by numeric ID (1-88) everywhere in Firestore.
 */

import { SPECIES_DATA, getSpeciesArray, getSpeciesByCategory, getSpeciesName } from './speciesData';
import { SPECIES_CATEGORIES } from './advertiserConstants';

// Re-export core functions
export { SPECIES_DATA, getSpeciesArray, getSpeciesByCategory, getSpeciesName };

/**
 * Get species grouped by category, for category-tabbed UI.
 * Returns: [{ category: { id, name, emoji }, species: [{ id, name, scientific, category }] }]
 */
export function getSpeciesGrouped() {
  const allSpecies = getSpeciesArray();
  return SPECIES_CATEGORIES.map(cat => ({
    category: cat,
    species: allSpecies.filter(s => s.category === cat.id),
  }));
}

/**
 * Get display names for an array of species IDs.
 * Used in app cards, push notification previews, etc.
 */
export function getSpeciesNames(ids = []) {
  return ids.map(id => getSpeciesName(id)).filter(n => n !== 'Unknown');
}

/**
 * Get short display string for species array.
 * e.g. "Humpback Whale, Orca, +3 more"
 */
export function getSpeciesSummary(ids = [], maxShow = 2) {
  const names = getSpeciesNames(ids);
  if (names.length === 0) return 'No species selected';
  if (names.length <= maxShow) return names.join(', ');
  return `${names.slice(0, maxShow).join(', ')} +${names.length - maxShow} more`;
}

/**
 * Check if two species arrays have any overlap.
 * Used for species-matching (user interests vs advertiser species).
 */
export function hasSpeciesOverlap(speciesA = [], speciesB = []) {
  const setA = new Set(speciesA);
  return speciesB.some(id => setA.has(id));
}

/**
 * Get category for a species ID.
 */
export function getSpeciesCategory(id) {
  const species = SPECIES_DATA[id];
  if (!species) return null;
  return SPECIES_CATEGORIES.find(c => c.id === species.category) || null;
}

/**
 * Migration helper: Convert old string species IDs to numeric IDs.
 * Maps the old hardcoded string IDs used in SpeciesSelectScreen and CompanyProfile
 * to the correct numeric IDs from speciesData.js.
 * 
 * Only needed during migration — new code always uses numeric IDs.
 */
export const LEGACY_SPECIES_MAP = {
  'humpback':  11,  // Humpback Whale
  'orca':      76,  // Killer Whale / Orca
  'gray':      10,  // Gray Whale
  'blue':      18,  // Blue Whale
  'sperm':     19,  // Sperm Whale
  'fin':       17,  // Fin Whale
  'minke':     12,  // Minke Whale
  'right':      5,  // North Atlantic Right Whale
  'beluga':    22,  // Beluga Whale
  'pilot':     77,  // Long-finned Pilot Whale
  'dolphin':   46,  // Bottlenose Dolphin
  'porpoise':  81,  // Harbour Porpoise
  'seal':      null, // Not in speciesData (pinnipeds) — skip
  'manatee':    1,  // West Indian Manatee
  'narwhal':   23,  // Narwhal
  'other':     null, // Generic — skip
};

/**
 * Convert legacy string species array to numeric IDs.
 * e.g. ['humpback', 'orca'] → [11, 76]
 */
export function migrateSpeciesIds(legacyIds = []) {
  return legacyIds
    .map(id => {
      if (typeof id === 'number') return id; // Already numeric
      return LEGACY_SPECIES_MAP[id] || null;
    })
    .filter(id => id !== null);
}

/**
 * Search species by name (for search/filter in species picker).
 */
export function searchSpecies(query = '') {
  if (!query.trim()) return getSpeciesArray();
  const lower = query.toLowerCase();
  return getSpeciesArray().filter(s =>
    s.name.toLowerCase().includes(lower) ||
    s.scientific.toLowerCase().includes(lower)
  );
}
